import {
  AIX_MODES,
  DASHBOARD_TYPES,
  DESIGN_PHILOSOPHY,
  EDP_ID,
  IA_HIERARCHY,
  NAV_GROUPS,
  NOTIFICATION_CATEGORIES,
  PERSONAS,
  WORKFLOW_PHASES,
  WORKSPACE_ZONES,
} from '../../experience/architecture';
import { EDP_FALLBACK_NAV } from '../../experience/navigation';

export function ExperienceArchitecturePage() {
  const moduleCount = EDP_FALLBACK_NAV.length;
  const groupCount = new Set(EDP_FALLBACK_NAV.map((n) => n.group)).size;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EDP_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900">Enterprise Experience Architecture</h1>
        <p className="mt-1 text-sm text-slate-500">
          Constitutional document for navigation, information architecture, workspace standards, and AI/human experience — single source of truth for every screen.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: 'Navigation Items', value: moduleCount.toString() },
          { label: 'Primary Groups', value: groupCount.toString() },
          { label: 'Personas', value: PERSONAS.length.toString() },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
            <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
          </div>
        ))}
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Design Philosophy</h2>
        <p className="mt-1 text-sm text-slate-500">Every design decision SHALL reference an approved philosophy pillar.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {DESIGN_PHILOSOPHY.map((p) => (
            <div key={p.id} className="rounded-lg bg-slate-50 p-3">
              <p className="font-medium text-slate-900">{p.label}</p>
              <p className="mt-1 text-xs text-slate-600">{p.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Information Architecture</h2>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {IA_HIERARCHY.map((level, i) => (
            <span key={level} className="flex items-center gap-2">
              {i > 0 && <span className="text-slate-300">→</span>}
              <span className="rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-700">{level}</span>
            </span>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900">Navigation Groups</h2>
        <div className="mt-4 space-y-4">
          {NAV_GROUPS.map((group) => {
            const items = EDP_FALLBACK_NAV.filter((n) => n.group === group);
            return (
              <div key={group}>
                <h3 className="text-sm font-semibold text-slate-700">{group}</h3>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {items.map((item) => (
                    <li key={item.id}>
                      <a href={item.path} className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-brand-300 hover:text-brand-700">
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Workspace Zones</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600">
            {WORKSPACE_ZONES.map((z) => <li key={z}>• {z}</li>)}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Workflow Experience</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {WORKFLOW_PHASES.map((phase, i) => (
              <span key={phase} className="flex items-center gap-1 text-sm">
                {i > 0 && <span className="text-slate-300">→</span>}
                <span className="rounded bg-violet-50 px-2 py-0.5 text-violet-700">{phase}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">AI Experience (AIX)</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600">
            {AIX_MODES.map((m) => <li key={m}>• {m}</li>)}
          </ul>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Personas</h2>
          <ul className="mt-3 space-y-2">
            {PERSONAS.map((p) => (
              <li key={p.id} className="text-sm">
                <span className="font-medium text-slate-900">{p.label}</span>
                <span className="text-slate-500"> — {p.focus}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Dashboard Types</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600">
            {DASHBOARD_TYPES.map((d) => <li key={d}>• {d}</li>)}
          </ul>
          <p className="mt-3 text-xs text-slate-500">Dashboards answer: What happened? Why? What should I do?</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Notification Categories</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600">
            {NOTIFICATION_CATEGORIES.map((n) => <li key={n}>• {n}</li>)}
          </ul>
        </div>
      </section>

      <section className="rounded-xl border border-brand-200 bg-brand-50 p-6">
        <h2 className="text-lg font-semibold text-brand-900">Governance</h2>
        <p className="mt-2 text-sm text-brand-800">
          No screen SHALL be designed without complying with EDP-00001. Implementation registry: <code className="rounded bg-white/60 px-1">pkg/experience/</code> (Go) and <code className="rounded bg-white/60 px-1">apps/web/src/experience/</code> (TypeScript). Full specification: <code className="rounded bg-white/60 px-1">docs/edp/EDP-00001_ENTERPRISE_EXPERIENCE_ARCHITECTURE.md</code>
        </p>
      </section>
    </div>
  );
}
